import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api, formatUSD, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ArrowDownRight, ArrowUpRight, Users, TrendingUp } from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";

function KPICard({ label, value, sub, icon: Icon, accent, testid, onClick }) {
  return (
    <Card
      data-testid={testid}
      onClick={onClick}
      className={`p-6 border-0 shadow-sm bg-card rounded-3xl transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${onClick ? "cursor-pointer" : ""}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
          <div className="font-serif text-4xl mt-3 tabular-nums">{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-2">{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${accent}`}>
          <Icon className="w-4 h-4" strokeWidth={2.2} />
        </div>
      </div>
    </Card>
  );
}

export default function DashboardPage() {
  const { activeOrgId, orgs } = useAuth();
  const navigate = useNavigate();
  const yearStart = `${new Date().getFullYear()}-01-01`;
  const today = new Date().toISOString().slice(0, 10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const activeOrg = orgs.find((o) => o.id === activeOrgId);

  useEffect(() => {
    if (!activeOrgId) return;
    setLoading(true);
    api.get(`/organizations/${activeOrgId}/dashboard`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [activeOrgId]);

  if (!activeOrgId) {
    return <div className="text-muted-foreground">No organization selected.</div>;
  }

  return (
    <div className="space-y-10 fade-up">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Dashboard</div>
          <h1 className="font-serif text-5xl tracking-tight mt-2">{activeOrg?.name || "Overview"}</h1>
          <p className="text-muted-foreground mt-2 max-w-md">
            Your year-to-date and this month at a glance.
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">As of</div>
          <div className="font-serif text-2xl tabular-nums">{formatDate(new Date().toISOString())}</div>
        </div>
      </header>

      {loading || !data ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-32" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <KPICard
              testid="kpi-ytd-income"
              label="YTD Income"
              value={formatUSD(data.ytd_income)}
              sub={`${formatUSD(data.mtd_income)} this month · click to view`}
              icon={ArrowUpRight}
              accent="bg-primary/10 text-primary"
              onClick={() => navigate(`/ledger?type=income&start=${yearStart}&end=${today}`)}
            />
            <KPICard
              testid="kpi-ytd-expense"
              label="YTD Expenses"
              value={formatUSD(data.ytd_expense)}
              sub={`${formatUSD(data.mtd_expense)} this month · click to view`}
              icon={ArrowDownRight}
              accent="bg-expense/10 text-expense"
              onClick={() => navigate(`/ledger?type=expense&start=${yearStart}&end=${today}`)}
            />
            <KPICard
              testid="kpi-ytd-net"
              label="Net Position"
              value={formatUSD(data.ytd_net)}
              sub={data.ytd_net >= 0 ? "In the black" : "Operating deficit"}
              icon={TrendingUp}
              accent={data.ytd_net >= 0 ? "bg-primary/10 text-primary" : "bg-destructive/10 text-destructive"}
              onClick={() => navigate(`/ledger?start=${yearStart}&end=${today}`)}
            />
            <KPICard
              testid="kpi-donors"
              label="Active Donors"
              value={String(data.donor_count)}
              sub="On record"
              icon={Users}
              accent="bg-secondary text-foreground/70"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2 p-6 border-0 shadow-sm bg-card rounded-3xl">
              <div className="flex items-baseline justify-between mb-6">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">12-Month Trend</div>
                  <h3 className="font-serif text-2xl mt-1">Income vs. Expenses</h3>
                </div>
                <div className="flex gap-4 text-xs">
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-primary" /> Income</span>
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-expense" /> Expenses</span>
                </div>
              </div>
              <div className="h-72" data-testid="dashboard-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.monthly_series} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="grad-inc" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(224 76% 48%)" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="hsl(224 76% 48%)" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="grad-exp" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(199 92% 56%)" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="hsl(199 92% 56%)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="hsl(220 22% 92%)" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" stroke="hsl(222 15% 50%)" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                    <YAxis stroke="hsl(222 15% 50%)" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip
                      formatter={(v) => formatUSD(v)}
                      contentStyle={{ background: "hsl(0 0% 100%)", border: "1px solid hsl(220 22% 90%)", borderRadius: 12, fontSize: 12 }}
                    />
                    <Area type="monotone" dataKey="income" stroke="hsl(224 76% 48%)" strokeWidth={2.2} fill="url(#grad-inc)" />
                    <Area type="monotone" dataKey="expense" stroke="hsl(199 92% 56%)" strokeWidth={2.2} fill="url(#grad-exp)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-6 border-0 shadow-sm bg-card rounded-3xl">
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">This Year</div>
              <h3 className="font-serif text-2xl mt-1">Top Expense Categories</h3>
              <div className="mt-6 h-72" data-testid="top-categories-chart">
                {data.top_categories.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                    No expenses yet.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.top_categories} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
                      <XAxis type="number" hide />
                      <YAxis type="category" dataKey="category" tick={{ fontSize: 12 }} width={110} tickLine={false} axisLine={false} />
                      <Tooltip formatter={(v) => formatUSD(v)} contentStyle={{ background: "hsl(0 0% 100%)", border: "1px solid hsl(220 22% 90%)", borderRadius: 12, fontSize: 12 }} />
                      <Bar dataKey="total" fill="hsl(199 92% 56%)" radius={[0, 12, 12, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Card>
          </div>

          <Card className="p-6 border-0 shadow-sm bg-card rounded-3xl" data-testid="recent-transactions-card">
            <div className="flex items-baseline justify-between mb-5">
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Recent activity</div>
                <h3 className="font-serif text-2xl mt-1">Latest transactions</h3>
              </div>
            </div>
            {data.recent_transactions.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground">No transactions yet. Add one from the Ledger.</div>
            ) : (
              <div className="divide-y divide-border">
                {data.recent_transactions.map((t) => (
                  <div key={t.id} className="py-3.5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <Badge
                        variant="outline"
                        className={
                          t.type === "income"
                            ? "bg-primary/10 text-primary border-primary/20"
                            : "bg-expense/10 text-expense border-expense/20"
                        }
                      >
                        {t.type}
                      </Badge>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{t.description || t.category_name || "—"}</div>
                        <div className="text-xs text-muted-foreground">
                          {formatDate(t.date)} · {t.category_name}{t.donor_name ? ` · ${t.donor_name}` : ""}
                        </div>
                      </div>
                    </div>
                    <div className={`font-mono text-sm tabular-nums ${t.type === "income" ? "text-primary" : "text-expense"}`}>
                      {t.type === "income" ? "+" : "−"}{formatUSD(t.amount)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
